import type { LiveData } from '../global/blocs/useLiveData'

type WaitingRoomType = 'simple' | 'image' | 'video'
type AudienceLiveState = {
  hasBeenBroadcasting: boolean
  isWebinarOver: boolean
  isWebinarTimePast: boolean
  hasRedirected: boolean
}

const isWebinarTimePastFromLiveData = (liveData: LiveData | undefined) =>
  !!liveData && liveData.live_date_epoch * 1000 < Date.now()

const resolveHasWitnessedLiveBroadcast = (
  hasBeenBroadcastingLatched: boolean,
  liveData: LiveData | undefined
) => hasBeenBroadcastingLatched || Boolean(liveData?.broadcasting_p)

const isWebinarOverFromLiveData = (
  liveData: LiveData | undefined,
  hasBeenBroadcastingLatched: boolean
) => {
  if (!liveData || liveData.broadcasting_p) {
    return false
  }

  const witnessedLive = resolveHasWitnessedLiveBroadcast(
    hasBeenBroadcastingLatched,
    liveData
  )

  return liveData.live_status === 'previous' || witnessedLive
}

const isFutureUpcomingWebinar = (liveData: LiveData) =>
  liveData.live_status === 'upcoming' &&
  !liveData.broadcasting_p &&
  !isWebinarTimePastFromLiveData(liveData)

const getNextAudienceLiveState = (
  currentState: AudienceLiveState,
  liveData: LiveData
): AudienceLiveState => {
  if (isFutureUpcomingWebinar(liveData)) {
    return {
      hasBeenBroadcasting: false,
      isWebinarOver: false,
      isWebinarTimePast: false,
      hasRedirected: false,
    }
  }

  return {
    ...currentState,
    hasBeenBroadcasting:
      currentState.hasBeenBroadcasting || liveData.broadcasting_p,
    isWebinarOver:
      currentState.isWebinarOver ||
      isWebinarOverFromLiveData(liveData, currentState.hasBeenBroadcasting),
    isWebinarTimePast:
      currentState.isWebinarTimePast || isWebinarTimePastFromLiveData(liveData),
  }
}

const shouldRedirectAfterWebinar = (
  liveData: LiveData | undefined,
  state: AudienceLiveState
) => {
  if (!liveData || !liveData.after_webinar_link || state.hasRedirected) {
    return false
  }

  // Only viewers who watched the broadcast end live get the redirect;
  // late joiners stay in the room to watch the on-demand recording.
  if (!state.hasBeenBroadcasting) {
    return false
  }

  return isWebinarOverFromLiveData(liveData, state.hasBeenBroadcasting)
}

const shouldShowAudienceWaitingRoom = (
  liveData: LiveData | undefined,
  isWebinarOver: boolean,
  hasWitnessedLiveBroadcast: boolean
) => {
  if (!liveData) {
    return false
  }

  const isOnDemand =
    liveData.enable_ondemand_p && liveData.live_status === 'previous'

  // Late joiners get on-demand replay; live watchers get the end experience.
  if (isOnDemand && !hasWitnessedLiveBroadcast) {
    return false
  }

  if (isWebinarOver) {
    return true
  }

  return !liveData.broadcasting_p
}

const normalizeRoomType = (type?: string): WaitingRoomType => {
  if (type === 'image' || type === 'video') {
    return type
  }

  return 'simple'
}

const getWaitingRoomType = (
  liveData: LiveData | undefined,
  isWebinarOver: boolean
): WaitingRoomType => {
  if (!liveData) {
    return 'simple'
  }

  if (isWebinarOver) {
    return normalizeRoomType(liveData.after_webinar_photo_type)
  }

  return normalizeRoomType(liveData.before_webinar_photo_type)
}

export {
  getNextAudienceLiveState,
  isWebinarOverFromLiveData,
  isWebinarTimePastFromLiveData,
  resolveHasWitnessedLiveBroadcast,
  shouldRedirectAfterWebinar,
  shouldShowAudienceWaitingRoom,
  getWaitingRoomType,
}
export type { AudienceLiveState, WaitingRoomType }
